import type { FeatureData } from "@/types/spectrum";
import { getCategoryLabel } from "./compoundCategory";
import { aggregateCategoriesByObservation } from "./observationGrouping";

/**
 * naturalConclusion — A-1 자연어 결론 derive 로직 (iter2.1).
 *
 * ConclusionStripV2 (AI 보조 분석 지표) 내부 zone 메시지 + 우세 카테고리 요약 도출.
 *
 * S23-E 경찰청 미팅 결정: "방화/자연발화" 단정 문구 전면 제거.
 * 인화성 물질 패턴 유사도 수치(RoMSH prob) → 중립적 정성 표현으로 변환.
 *
 * 모든 zone 문구는 단정형 회피 + "전문가 검토" / "추가 검토" caveat 포함 (Q4 결정).
 *
 * 2026-09-23: 우리가 정한 확률 경계(0.30/0.70)로 문장을 고르던 방식을 버리고,
 * 모델이 기록한 판정(romsh_label)을 그대로 옮긴다. 근거는
 * docs/2026/260922_인하대_RoMSH_v3_검토_재현.md §1-7·§8 결정 3 — 모델 임계값은
 * 0.951 인데 화면이 0.5·0.3/0.7 로 다시 갈라 모델과 다른 말을 하고 있었다.
 */

const MAX_CATEGORIES_DISPLAYED = 3;

/** 모델이 기록한 판정 — romsh_label 1 / 0 / 없음 */
export type RomshDecision = "similar" | "not_similar" | "none";

export interface NaturalConclusion {
  zoneMessage: string;
  dominantCategoriesSummary: string;
}

function deriveZoneMessage(decision: RomshDecision): string {
  if (decision === "similar") {
    return "모델은 인화성 물질 패턴과 유사하다고 판정했습니다. 전문가 검토가 필요합니다.";
  }
  if (decision === "not_similar") {
    return "모델은 인화성 물질 패턴과 유사하지 않다고 판정했습니다. 추가 검토를 권고합니다.";
  }
  return "RoMSH 판정 기록 없음 — 재분석 또는 추가 검토가 필요합니다.";
}

/**
 * T1: 카테고리 건수는 **관찰(스캔) 단위**로 센다. 같은 스캔의 후보 여러 개
 * (Ethylbenzene·Xylene 3종…)를 "방향족 5건" 으로 세면 증거가 중복된다.
 * 후보 수가 관찰 수보다 많으면 괄호로 따로 밝힌다.
 */
function deriveDominantCategoriesSummary(features: FeatureData[]): string {
  if (features.length === 0) {
    return "AI 후보 없음";
  }
  const agg = aggregateCategoriesByObservation(features);
  if (agg.size === 0) {
    return "AI 후보 없음";
  }
  const top = Array.from(agg.entries()).slice(0, MAX_CATEGORIES_DISPLAYED);
  const parts = top.map(([category, count]) => {
    const label = `${getCategoryLabel(category)} 관찰 ${count.observations}건`;
    return count.candidates > count.observations
      ? `${label}(후보 ${count.candidates})`
      : label;
  });
  return `우세 후보 단서: ${parts.join(", ")}`;
}

/**
 * 모델 판정 + features 입력 → 자연어 결론 2줄 derive.
 *
 * @param decision 모델이 기록한 판정 (romsh_label 기반, none = 기록 없음)
 * @param features 검출 화합물 배열
 */
export function deriveNaturalConclusion(
  decision: RomshDecision,
  features: FeatureData[],
): NaturalConclusion {
  return {
    zoneMessage: deriveZoneMessage(decision),
    dominantCategoriesSummary: deriveDominantCategoriesSummary(features),
  };
}
